import { DynamicFlight, GdsFlyAir, GrossAndTax, Outbound } from 'ts/dynamic/interfaces/DynamicResponse';
import { FlightFilterModel, TimeFilter } from 'ts/dynamic/classes/DynamicFilterFlight';
import { formatPriceCurrency, getDurationTicks } from 'utils/utils';
import { getNoBaggage } from './dynamic';

export const getStopsOptions = (oneWay: boolean) => {
  if (oneWay) {
    return [
      { number: 0, label: 'filter.direct_flights' },
      { number: 1, label: 'filter.one_stop' },
      { number: 2, label: 'filter.two_or_more_stops' },
    ];
  } else {
    return [
      { number: 0, label: 'filter.direct_flights' },
      { number: 0.5, label: 'filter.direct_flights_outbound' },
      { number: 1, label: 'filter.one_stop' },
      { number: 2, label: 'filter.two_or_more_stops' },
    ];
  }
};

export const getBounds = (flight: DynamicFlight): Outbound[] => {
  if (flight.outbound && flight.inbound) {
    return [flight.outbound, { ...flight.inbound, flightType: 'return' }];
  } else if (flight.flightBounds?.length) {
    return flight.flightBounds;
  } else {
    return [flight.outbound];
  }
};

export const getStopsInbound = (flights: DynamicFlight[], currencySign: string) => {
  return getStopsOptions(false).map((stop) => {
    const prices = flights
      .filter((flight) => {
        const outbondStopsCount = flight.outbound?.legCount - 1;
        const inboundStopsCount = flight.inbound?.legCount - 1;
        if (stop.number === 0.5) {
          return outbondStopsCount === 0 && inboundStopsCount > 0;
        } else if (stop.number === 2) {
          return outbondStopsCount >= stop.number && inboundStopsCount >= stop.number;
        } else {
          return stop.number === outbondStopsCount && stop.number === inboundStopsCount;
        }
      })
      .map((flight) => flight.priceGrossAndTax);

    const minPrice = prices.length ? Math.min(...prices) : 0;

    return {
      ...stop,
      minPrice: formatPriceCurrency(minPrice, currencySign),
      checked: false,
      disabled: !prices.length,
    };
  });
};

export const getBaggage = (flights: DynamicFlight[], currencySign: string) => {
  const checkBaggage = (bounds: Outbound[], baggage: any) => {
    if (baggage.number === 0) {
      return bounds.every((bound) => getNoBaggage(bound));
    } else if (baggage.number === 1) {
      return bounds.some((bound) => !getNoBaggage(bound));
    }
    return false;
  };
  return [
    { number: 0, label: 'filter.baggage_not_included' },
    { number: 1, label: 'filter.baggage_included' },
  ].map((baggage) => {
    const prices = flights
      .filter((flight) => {
        const bounds = getBounds(flight);
        return checkBaggage(bounds, baggage);
      })
      .map((flight) => flight.priceGrossAndTax);

    const minPrice = prices.length ? Math.min(...prices) : 0;
    return {
      ...baggage,
      minPrice: formatPriceCurrency(minPrice, currencySign),
      checked: false,
      disabled: !prices.length,
    };
  });
};

export const getAirlines = (flights: DynamicFlight[], currencySign: string) => {
  return flights
    .map((flight) => flight.airlineCode)
    .filter((airline, index, arr) => arr.indexOf(airline) === index)
    .map((airline) => {
      const minPrice = Math.min(
        ...flights
          .filter((flight) => flight.airlineCode === airline)
          .map((flight) => flight.priceGrossAndTax)
      );

      const label = flights.find((flight) => flight.airlineCode === airline)?.airlineDesc || '';

      return {
        code: airline || '',
        label,
        checked: false,
        minPrice: formatPriceCurrency(minPrice, currencySign),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const getAirports = (flights: DynamicFlight[], currencySign: string) => {
  const airportCodes = new Set<string>();

  flights.forEach((flight) => {
    getBounds(flight).forEach((bound) => {
      airportCodes.add(bound.departureAirport.code);
      airportCodes.add(bound.arrivalAirport.code);
    });
  });

  return Array.from(airportCodes)
    .map((airport) => {
      const relevantFlights = flights.filter((flight) =>
        getBounds(flight).some(
          (bound) =>
            bound.departureAirport.code === airport || bound.arrivalAirport.code === airport
        )
      );

      const minPrice = Math.min(...relevantFlights.map((flight) => flight.priceGrossAndTax));

      const labelFlight = relevantFlights[0];
      let label = airport;
      if (labelFlight) {
        const labelBound = getBounds(labelFlight).find(
          (bound) =>
            bound.departureAirport.code === airport || bound.arrivalAirport.code === airport
        );
        const labelAirport =
          labelBound?.departureAirport.code === airport
            ? labelBound?.departureAirport
            : labelBound?.arrivalAirport;

        label = labelAirport?.desc ? `${labelAirport.desc}, ${labelAirport.cityDesc}` : airport;
      }
      return {
        code: airport,
        label,
        checked: false,
        minPrice: formatPriceCurrency(minPrice, currencySign),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const getFlightTypes = (flights: DynamicFlight[], currencySign: string) => {
  return flights
    .map((flight) => flight.flightSource)
    .filter((type, index, arr) => arr.indexOf(type) === index && type)
    .map((type) => {
      const minPrice = Math.min(
        ...flights
          .filter((flight) => flight.flightSource === type)
          .map((flight) => flight.priceGrossAndTax)
      );

      return {
        code: type || '',
        label: type,
        minPrice: formatPriceCurrency(minPrice, currencySign),
        checked: false,
      };
    });
};

export const getTimes = (times: number[]): TimeFilter => {
  const minArrivalTime = Math.floor(times.length ? Math.min(...times) : 0);
  const maxArrivalTime = Math.ceil(times.length ? Math.max(...times) : 1);

  return {
    min: minArrivalTime,
    max: maxArrivalTime,
    value: [minArrivalTime, maxArrivalTime],
  };
};

export const initFlights = (
  flights: DynamicFlight[],
  selectedPrice: number,
  currency: keyof GrossAndTax
) => {
  return flights.map((flight) => {
    return {
      ...flight,
      outbound: getBound(flight.outbound),
      inbound: getBound(flight.inbound),
      priceDiff:
        ((flight.flighPrice.grossAndTax[currency] as number) - selectedPrice) /
        flight.flightPricesByPDT[0].quantity,
      visible: true,
    };
  });
};

export const initFlight = (flight: DynamicFlight) => {
  return {
    ...flight,
    outbound: getBound(flight.outbound),
    inbound: getBound(flight.inbound),
  };
};

const getBound = (bound: Outbound) => {
  if (bound) {
    let gdsFlyAirs: GdsFlyAir[] = [
      ...bound.gdsFlyAirs.map((gds) => ({ ...gds, stopDuration: '0' })),
    ];
    if (bound.gdsFlyAirs.length > 1) {
      for (let i = 0; i < bound.gdsFlyAirs.length - 1; i++) {
        const stopDuration = getDurationTicks(
          bound.gdsFlyAirs[i].arrivalDateTime,
          bound.gdsFlyAirs[i + 1].departureDateTime
        );
        gdsFlyAirs[i].stopDuration = stopDuration;
      }
    }
    return {
      ...bound,
      gdsFlyAirs,
    };
  } else {
    return bound;
  }
};

export const filterDynamicFlights = (flights: DynamicFlight[], filter: FlightFilterModel) => {
  return flights.map((flight) => {
    const checkedStops = filter.stops.filter((stop) => stop.checked);
    const filteredByStops =
      checkedStops.length === 0 ||
      checkedStops.some((stop) => {
        const outbondStopsCount = flight.outbound?.legCount - 1;
        const inboundStopsCount = flight.inbound?.legCount - 1;
        if (isNaN(inboundStopsCount)) {
          return stop.number === outbondStopsCount;
        } else {
          if (stop.number === 0.5) {
            return outbondStopsCount === 0 && inboundStopsCount > 0;
          } else {
            return stop.number === outbondStopsCount && stop.number === inboundStopsCount;
          }
        }
      });

    let filteredByBaggage = true;

    if (filter.baggage[1].checked && filter.baggage[0].checked) {
    } else if (filter.baggage[0].checked) {
      filteredByBaggage = getNoBaggage(flight.outbound) && getNoBaggage(flight.inbound);
    } else if (filter.baggage[1].checked) {
      filteredByBaggage = !getNoBaggage(flight.outbound) && !getNoBaggage(flight.inbound);
    }

    const filteredByPrice =
      filter.price.value[0] <= flight.priceDiff && filter.price.value[1] >= flight.priceDiff;

    /// TIME  ///
    let filteredByDepartureTime = true;
    let filteredByArrivalTime = true;
    if (filter.departureOutboundTime && filter.arrivalOutboundTime) {
      const departureOutboundTime = new Date(flight.outbound.departureDateTime).getTime();
      filteredByDepartureTime =
        filter.departureOutboundTime.value[0] <= departureOutboundTime &&
        filter.departureOutboundTime.value[1] >= departureOutboundTime;

      const arrivalOutboundTime = new Date(flight.outbound.arrivalDateTime).getTime();
      filteredByArrivalTime =
        filter.arrivalOutboundTime.value[0] <= arrivalOutboundTime &&
        filter.arrivalOutboundTime.value[1] >= arrivalOutboundTime;
    }

    let filteredByDepartureInboundTime = true;
    let filteredByArrivalInboundTime = true;
    if (filter.departureInboundTime && filter.arrivalInboundTime) {
      const departureInboundTime = new Date(flight.inbound?.departureDateTime).getTime();
      filteredByDepartureInboundTime =
        filter.departureInboundTime.value[0] <= departureInboundTime &&
        filter.departureInboundTime.value[1] >= departureInboundTime;

      const arrivalInboundTime = new Date(flight.inbound?.arrivalDateTime).getTime();
      filteredByArrivalInboundTime =
        filter.arrivalInboundTime.value[0] <= arrivalInboundTime &&
        filter.arrivalInboundTime.value[1] >= arrivalInboundTime;
    }

    /// DURATION ///
    const filteredByDuration =
      !flight.outbound.elapsedDTime ||
      (filter.duration.value[0] <= flight.outbound.elapsedDTime &&
        filter.duration.value[1] >= flight.outbound.elapsedDTime);

    const filteredByStopDuration =
      durationGdsCheck(filter.stopDuration.value, flight.outbound.gdsFlyAirs) ||
      durationGdsCheck(filter.stopDuration.value, flight.inbound?.gdsFlyAirs);

    const checkedAirlines = filter.airlines.filter((airline) => airline.checked);
    const filteredByAirline =
      !checkedAirlines.length ||
      checkedAirlines.some((airline) => airline.code === flight.airlineCode);

    const checkedAirports = filter.airports.filter((airport) => airport.checked);
    const filteredByAirport =
      !checkedAirports.length ||
      checkedAirports.some(
        (airport) =>
          airport.code === flight.outbound.departureAirport.code ||
          airport.code === flight.outbound.arrivalAirport.code ||
          airport.code === flight.inbound?.departureAirport.code ||
          airport.code === flight.inbound?.arrivalAirport.code
      );

    const checkedFlightTypes = filter.flightTypes.filter((flightType) => flightType.checked);
    const filteredByFlightType =
      !checkedFlightTypes.length ||
      checkedFlightTypes.some((flightType) => flightType.label === flight.flightSource);

    const visible =
      filteredByStops &&
      filteredByBaggage &&
      filteredByPrice &&
      filteredByDepartureTime &&
      filteredByArrivalTime &&
      filteredByDepartureInboundTime &&
      filteredByArrivalInboundTime &&
      filteredByDuration &&
      filteredByStopDuration &&
      filteredByAirline &&
      filteredByFlightType &&
      filteredByAirport;

    return {
      ...flight,
      visible,
    };
  });
};
const durationGdsCheck = (stopDurationValue: [number, number], flyAirs?: GdsFlyAir[]) => {
  if (!flyAirs) return true;
  if (flyAirs.length > 1 && stopDurationValue[0] > 0) {
    const newFlyAirs = flyAirs.filter((flyAir) => parseInt(flyAir.stopDuration) > 0);
    return newFlyAirs.some(
      (flyAir) =>
        parseInt(flyAir.stopDuration) >= stopDurationValue[0] &&
        parseInt(flyAir.stopDuration) <= stopDurationValue[1]
    );
  } else if (flyAirs.length === 1 && stopDurationValue[0] > 0) {
    return false;
  }

  return true;
};
